import { DefaultThemeId, ReaderFontSizes } from "../theme/theme";
import { getSystemLang } from "../user-agent";
import { DEFAULT_LANGUAGE } from "../multilang/constants";
import { SearchFontSizeDefault, TreeFontSizeDefault, VIEW_MODE } from "../viewmode";
import {
  IdReaderDefaultFont,
  DEF_READER_TEXT_ALIGN,
  READER_OPEN_LINK_BEHAVIOUR,
  READER_PLAYER_BEHAVIOURS
} from "../reader/constants";
import { SearchPanelTabs } from "../../content/search";
import { DEFAULT_BIBLE } from "../content/content.constants";
import {
  dictionaryNWANDFilterKey,
  Order,
  PeriodDefault,
  QUERY_TYPE,
  Snippet,
  ViewTabResultsRightPanelDefault
} from "../search/search.constants";
import { MENU_STATE_COLLAPSE, TOGGLE_IN_LIBRARY_BEHAVIOURS, TreeMenuMode } from "../../constants";
import { SAVE_WORKSPACE_BEHAVIOURS } from "../workspace/constants";

export const SETTING_LOCATION = {
  MAIN_APP: "MAIN_APP",
  NEXT_APP: "NEXT_APP"
};

export const SETTING_TYPE = {
  string: "string",
  bool: "bool",
  number: "number",
  array: "array",
  object: "object",
};

// TODO:
//  Possible REFACTOR ways:
//  #1 way: add validation and default values as object to this constant,
//    so everything about settings will be in the single place
//    (but there for each app would be redundant data of other apps config)
//  #2 way: – {locations} should be removed, instead each app determined what settings it supports
//          – {default} values should be moved to {SHARED_DEFAULT_SETTINGS} object,
//            and each application should implement its {DEFAULT_SETTINGS} object,
//            that is inherited from the shared object.
export const SETTING = {
  isDevMode: { // for show some features on demo servers after enable
    id: "isDevMode",
    default: false,
    type: SETTING_TYPE.bool,
    locations: [
      SETTING_LOCATION.MAIN_APP,
      SETTING_LOCATION.NEXT_APP
    ]
  },
  themeMode: {
    id: "themeMode",
    server: true,
    type: SETTING_TYPE.string,
    default: DefaultThemeId,
    saveOnLogout: true,
    locations: [
      SETTING_LOCATION.MAIN_APP,
      SETTING_LOCATION.NEXT_APP
    ]
  },
  textMode: {
    id: "textMode",
    type: SETTING_TYPE.bool,
    default: false,
    locations: [
      SETTING_LOCATION.MAIN_APP,
      SETTING_LOCATION.NEXT_APP
    ]
  },
  languageUI: {
    id: "languageUI",
    server: true,
    type: SETTING_TYPE.string,
    default: getSystemLang(DEFAULT_LANGUAGE),
    locations: [
      SETTING_LOCATION.MAIN_APP,
      SETTING_LOCATION.NEXT_APP
    ]
  },
  isGrid: {
    id: "isGrid",
    type: SETTING_TYPE.bool,
    default: true,
    locations: [
      SETTING_LOCATION.MAIN_APP,
      SETTING_LOCATION.NEXT_APP
    ]
  },
  isShowAZ: {
    id: "isShowAZ",
    type: SETTING_TYPE.bool,
    default: true,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  isShowRightPanel: {
    id: "isShowRightPanel",
    type: SETTING_TYPE.bool,
    default: false,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  zoom: {
    id: "zoom",
    server: true,
    type: SETTING_TYPE.number,
    default: 100,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  treeFontSize: {
    id: "treeFontSize",
    server: true,
    type: SETTING_TYPE.number,
    default: TreeFontSizeDefault,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  searchFontSize: {
    id: "searchFontSize",
    server: true,
    type: SETTING_TYPE.number,
    default: SearchFontSizeDefault,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  showSCEntriesInReader: {
    id: "showSCEntriesInReader",
    type: SETTING_TYPE.bool,
    default: true,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  lastActiveColor: {
    id: "lastActiveColor",
    server: true,
    type: SETTING_TYPE.string,
    default: undefined,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  editorTempEntry: {
    id: "editorTempEntry",
    server: true,
    type: SETTING_TYPE.object,
    default: {},
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  showTranslations: {
    id: "showTranslations",
    server: true,
    type: SETTING_TYPE.bool,
    default: true,
    locations: [
      SETTING_LOCATION.MAIN_APP,
      SETTING_LOCATION.NEXT_APP,
    ]
  },
  showRefCodes: {
    id: "showRefCodes",
    server: true,
    type: SETTING_TYPE.bool,
    default: true,
    locations: [
      SETTING_LOCATION.MAIN_APP,
      SETTING_LOCATION.NEXT_APP,
    ]
  },
  showHeaders: {
    id: "showHeaders",
    server: true,
    type: SETTING_TYPE.bool,
    default: false,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  showPageNumbers: {
    id: "showPageNumbers",
    server: true,
    type: SETTING_TYPE.bool,
    default: false,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  showPageBreaks: {
    id: "showPageBreaks",
    server: true,
    type: SETTING_TYPE.bool,
    default: false,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  syncReader: {
    id: "syncReader",
    type: SETTING_TYPE.bool,
    default: false,
    locations: [
      SETTING_LOCATION.MAIN_APP,
      SETTING_LOCATION.NEXT_APP,
    ]
  },
  syncReaderAndSearch: {
    id: "syncReaderAndSearch",
    type: SETTING_TYPE.bool,
    default: true,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  syncPlayerAndReader: {
    id: "syncPlayerAndReader",
    type: SETTING_TYPE.bool,
    default: false,
    locations: [
      SETTING_LOCATION.MAIN_APP,
      SETTING_LOCATION.NEXT_APP,
    ]
  },
  readerFontFamily: {
    id: "readerFontFamily",
    server: true,
    type: SETTING_TYPE.string,
    default: IdReaderDefaultFont,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  readerFontSize: {
    id: "readerFontSize",
    server: true,
    type: SETTING_TYPE.number,
    default: ReaderFontSizes.default,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  textAlign: {
    id: "textAlign",
    server: true,
    type: SETTING_TYPE.string,
    default: DEF_READER_TEXT_ALIGN,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  readerMinWidth: {
    id: "readerMinWidth",
    type: SETTING_TYPE.number,
    default: 300,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  layout: {
    id: "layout",
    type: SETTING_TYPE.string,
    default: "",
    locations: [
      SETTING_LOCATION.MAIN_APP,
      SETTING_LOCATION.NEXT_APP,
    ]
  },
  isShowFooter: {
    id: "isShowFooter",
    type: SETTING_TYPE.bool,
    default: true,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  isShowFooterContent: {
    id: "isShowFooterContent",
    type: SETTING_TYPE.bool,
    default: false,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  scrollToFooter: {
    id: "scrollToFooter",
    type: SETTING_TYPE.bool,
    default: false,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  viewMode: {
    id: "viewMode",
    type: SETTING_TYPE.string,
    default: VIEW_MODE.DESKTOP,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  rightTab: {
    id: "rightTab",
    type: SETTING_TYPE.string,
    default: SearchPanelTabs.RESULTS,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  showRightPanel: {
    id: "showRightPanel",
    type: SETTING_TYPE.bool,
    default: false,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  isRightPanelPinned: {
    id: "isRightPanelPinned",
    type: SETTING_TYPE.bool,
    default: false,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  showLeftPanel: {
    id: "showLeftPanel",
    type: SETTING_TYPE.bool,
    default: false,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  isLeftPanelPinned: {
    id: "isLeftPanelPinned",
    type: SETTING_TYPE.bool,
    default: false,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  openLastBook: {
    id: "openLastBook",
    server: true,
    type: SETTING_TYPE.bool,
    default: false,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  libraryLanguages: {
    id: "libraryLanguages",
    type: SETTING_TYPE.array,
    default: [],
    locations: [
      SETTING_LOCATION.MAIN_APP,
      SETTING_LOCATION.NEXT_APP
    ]
  },
  filterLibraryLanguages: {
    id: "filterLibraryLanguages",
    type: SETTING_TYPE.array,
    default: [],
    locations: [
      SETTING_LOCATION.MAIN_APP
    ]
  },
  sortByRecent: {
    id: "sortByRecent",
    server: true,
    type: SETTING_TYPE.bool,
    default: true,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  playerState: {
    id: "playerState",
    type: SETTING_TYPE.number,
    locations: [
      SETTING_LOCATION.MAIN_APP,
      SETTING_LOCATION.NEXT_APP,
    ]
  },
  baseBible: {
    id: "baseBible",
    type: SETTING_TYPE.string,
    server: true,
    default: "b" + DEFAULT_BIBLE.en,
    locations: [
      SETTING_LOCATION.MAIN_APP,
      SETTING_LOCATION.NEXT_APP
    ]
  },
  jtlBaseBible: {
    id: "jtlBaseBible",
    type: SETTING_TYPE.string,
    server: true,
    default: "b1965",
    locations: [
      SETTING_LOCATION.MAIN_APP,
      SETTING_LOCATION.NEXT_APP
    ]
  },
  timeZoneAuto: {
    id: "timeZoneAuto",
    server: true,
    type: SETTING_TYPE.bool,
    default: true,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  timeZone: {
    id: "timeZone",
    server: true,
    type: SETTING_TYPE.string,
    default: "America/New_York",
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  collectionFilter: {
    id: "collectionFilter",
    server: true,
    type: SETTING_TYPE.string,
    default: PeriodDefault,
    locations: [
      SETTING_LOCATION.MAIN_APP,
      SETTING_LOCATION.NEXT_APP
    ],
  },
  dictionaryFilter: {
    id: "dictionaryFilter",
    server: true,
    type: SETTING_TYPE.string,
    default: dictionaryNWANDFilterKey,
    locations: [
      SETTING_LOCATION.MAIN_APP,
      SETTING_LOCATION.NEXT_APP
    ],
  },
  deliveryTime: {
    id: "deliveryTime",
    type: SETTING_TYPE.string,
    default: "00:00:00",
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  searchLangDefault: {
    id: "searchLangDefault",
    server: true,
    type: SETTING_TYPE.string,
    default: getSystemLang(DEFAULT_LANGUAGE),
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  searchSnippet: {
    id: "searchSnippet",
    server: true,
    type: SETTING_TYPE.string,
    default: Snippet.LONG,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  searchSort: {
    id: "searchSort",
    server: true,
    type: SETTING_TYPE.string,
    default: Order.wrel,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  searchQueryType: {
    id: "searchQueryType",
    server: true,
    type: SETTING_TYPE.string,
    default: QUERY_TYPE.PREFER_EXACT,
    locations: [
      SETTING_LOCATION.MAIN_APP,
      SETTING_LOCATION.NEXT_APP,
    ]
  },
  viewTabResultsRightPanel: {
    id: "viewTabResultsRightPanel",
    server: false,
    type: SETTING_TYPE.string,
    default: ViewTabResultsRightPanelDefault,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  showBookshelf: {
    id: "showBookshelf",
    type: SETTING_TYPE.bool,
    default: true,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  lockedTabs: {
    id: "lockedTabs",
    type: SETTING_TYPE.array,
    default: [],
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  menuState: {
    id: "menuState",
    type: SETTING_TYPE.string,
    default: MENU_STATE_COLLAPSE,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  hlListType: {
    id: "hlListType",
    type: SETTING_TYPE.string,
    default: "g_c",
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  scPanel: {
    id: "scPanel",
    type: SETTING_TYPE.string,
    default: "n",
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  libraryFirstOpen: {
    id: "libraryFirstOpen",
    type: SETTING_TYPE.bool,
    default: true,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  isWSConfirmFirstOpened: {
    id: "isWSConfirmFirstOpened",
    type: SETTING_TYPE.bool,
    default: true,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  paraChangePlayerBehaviour: {
    id: "paraChangePlayerBehaviour",
    server: true,
    type: SETTING_TYPE.number,
    default: READER_PLAYER_BEHAVIOURS.ASK,
    locations: [
      SETTING_LOCATION.MAIN_APP,
      SETTING_LOCATION.NEXT_APP,
    ]
  },
  bookChangePlayerBehaviour: {
    id: "bookChangePlayerBehaviour",
    server: true,
    type: SETTING_TYPE.number,
    default: READER_PLAYER_BEHAVIOURS.ASK,
    locations: [
      SETTING_LOCATION.MAIN_APP,
      SETTING_LOCATION.NEXT_APP,
    ]
  },
  saveWSOnReaderCloseBehaviour: {
    id: "saveWSOnReaderCloseBehaviour",
    server: true,
    type: SETTING_TYPE.number,
    default: SAVE_WORKSPACE_BEHAVIOURS.ASK,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  toggleInLibraryBehaviour: {
    id: "toggleInLibraryBehaviour",
    server: true,
    type: SETTING_TYPE.number,
    default: TOGGLE_IN_LIBRARY_BEHAVIOURS.ASK,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  copyChecks: {
    id: "copyChecks",
    type: SETTING_TYPE.object,
    default: {
      refs: true,
      headers: false,
      pageBreaks: false,
      scElements: false,
      searchHighlights: false,
      embedUrlLink: false,
      footnote: false,
    },
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  printChecks: {
    id: "printChecks",
    type: SETTING_TYPE.object,
    default: {
      refs: true,
      headers: false,
      pageBreaks: false,
      format: true,
      radio: "para"
    },
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  workspaces: {
    id: "workspaces",
    server: true,
    type: SETTING_TYPE.array,
    default: [],
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  activeWorkspace: {
    id: "activeWorkspace",
    server: true,
    type: SETTING_TYPE.string,
    default: "",
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  lastEditWorkspace: {
    id: "lastEditWorkspace",
    server: true,
    type: SETTING_TYPE.string,
    default: "",
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  treeMenuMode: {
    id: "treeMenuMode",
    type: SETTING_TYPE.string,
    default: TreeMenuMode.FOLDERS,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  showReadingHistory: {
    id: "showReadingHistory",
    type: SETTING_TYPE.bool,
    default: true,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  showListenHistory: {
    id: "showListenHistory",
    type: SETTING_TYPE.bool,
    default: true,
    locations: [
      SETTING_LOCATION.MAIN_APP,
    ]
  },
  geoIpData: {
    id: "geoIpData",
    saveOnLogout: true,
    locations: []
  },

  openLink: {
    id: "openLink",
    server: true,
    locations: [SETTING_LOCATION.MAIN_APP],
    default: READER_OPEN_LINK_BEHAVIOUR.openInActiveSplit,
    type: SETTING_TYPE.string,
  }
};
